import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, ChevronRight, X, Clock, Video, Calendar, FileText, ExternalLink } from 'lucide-react';
import { fmtTime, fmtTimeRange } from '../../lib/datetime';

interface Module {
  id: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  zoom_link: string | null;
  course_id: string;
  courses: {
    name: string;
  };
}

interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  start_time: string;
  event_type: string;
  module_id: string | null;
  courses?: {
    name: string;
  };
}

interface DayData {
  date: Date;
  modules: Module[];
  events: CalendarEvent[];
  isCurrentMonth: boolean;
}

export function CalendarView() {
  const { profile } = useAuth();
  const [modules, setModules] = useState<Module[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedItem, setSelectedItem] = useState<Module | CalendarEvent | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadSchedule();
    // Dep en profile?.id (string), no en profile (objeto) — evita recargar
    // el calendario completo cada vez que Supabase refresca el token al
    // volver de otra ventana.
  }, [profile?.id, profile?.role]);

  const loadSchedule = async () => {
    if (!profile) return;

    try {
      setLoading(true);

      if (profile.role === 'alumno') {
        const { data: enrollments } = await supabase
          .from('course_enrollments')
          .select('course_id, courses!inner(status)')
          .eq('alumno_id', profile.id)
          .eq('status', 'active')
          .eq('courses.status', 'active');

        const courseIds = enrollments?.map(e => e.course_id) || [];

        if (courseIds.length > 0) {
          const { data: modulesData } = await supabase
            .from('modules')
            .select(`
              id,
              name,
              description,
              start_date,
              end_date,
              zoom_link,
              course_id,
              courses (name)
            `)
            .in('course_id', courseIds)
            .not('start_date', 'is', null)
            .order('start_date', { ascending: true });

          setModules(modulesData || []);

          const { data: eventsData } = await supabase
            .from('calendar_events')
            .select(`
              id,
              title,
              description,
              start_time,
              event_type,
              module_id,
              courses (name)
            `)
            .in('course_id', courseIds)
            .in('event_type', ['deadline', 'exam'])
            .order('start_time', { ascending: true });

          setEvents(eventsData || []);
        }
      } else {
        const { data: modulesData } = await supabase
          .from('modules')
          .select(`
            id,
            name,
            description,
            start_date,
            end_date,
            zoom_link,
            course_id,
            courses (name)
          `)
          .not('start_date', 'is', null)
          .order('start_date', { ascending: true });

        setModules(modulesData || []);

        const { data: eventsData } = await supabase
          .from('calendar_events')
          .select(`
            id,
            title,
            description,
            start_time,
            event_type,
            module_id,
            courses (name)
          `)
          .in('event_type', ['deadline', 'exam'])
          .order('start_time', { ascending: true });

        setEvents(eventsData || []);
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (): DayData[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    const days: DayData[] = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dayModules = modules.filter(m => {
        const moduleDate = new Date(m.start_date);
        return moduleDate.getFullYear() === current.getFullYear() &&
               moduleDate.getMonth() === current.getMonth() &&
               moduleDate.getDate() === current.getDate();
      });

      const dayEvents = events.filter(e => {
        const eventDate = new Date(e.start_time);
        return eventDate.getFullYear() === current.getFullYear() &&
               eventDate.getMonth() === current.getMonth() &&
               eventDate.getDate() === current.getDate();
      });

      days.push({
        date: new Date(current),
        modules: dayModules,
        events: dayEvents,
        isCurrentMonth: current.getMonth() === month
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  const isModule = (item: Module | CalendarEvent): item is Module => {
    return 'start_date' in item;
  };

  const handleItemClick = (item: Module | CalendarEvent) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const days = getDaysInMonth();
  const monthName = currentDate.toLocaleDateString('es', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 p-4 sm:p-6 lg:p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cubus-lime"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-display text-3xl font-bold text-white">Calendario</h1>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-white/[0.06] rounded-lg transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-muted" />
          </button>
          <h2 className="text-xl font-bold text-white capitalize min-w-[200px] text-center">
            {monthName}
          </h2>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-white/[0.06] rounded-lg transition-all"
          >
            <ChevronRight className="w-6 h-6 text-muted" />
          </button>
        </div>
      </div>

      <div className="glass-surface rounded-xl border border-white/[0.06] overflow-hidden flex-1 flex flex-col">
        <div className="grid grid-cols-7 border-b border-white/[0.06] flex-shrink-0">
          {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((day) => (
            <div
              key={day}
              className="p-3 text-center font-semibold text-muted glass-deep"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 flex-1">
          {days.map((day, index) => {
            const isToday =
              day.date.getDate() === new Date().getDate() &&
              day.date.getMonth() === new Date().getMonth() &&
              day.date.getFullYear() === new Date().getFullYear();

            const hasDeadline = day.events.length > 0;

            return (
              <div
                key={index}
                className={`p-3 border-r border-b border-white/[0.06] flex flex-col ${
                  !day.isCurrentMonth ? 'bg-[#1A1D21]' : 'glass-surface'
                } ${isToday ? 'ring-2 ring-cubus-lime ring-inset' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-sm font-semibold ${
                      isToday
                        ? 'text-cubus-lime'
                        : day.isCurrentMonth
                        ? 'text-white'
                        : 'text-faint'
                    }`}
                  >
                    {day.date.getDate()}
                  </span>
                  {hasDeadline && (
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  )}
                </div>

                <div className="space-y-1">
                  {day.modules.slice(0, 3).map((module) => (
                    <button
                      key={module.id}
                      onClick={() => handleItemClick(module)}
                      className="w-full text-left p-2 rounded glass-deep hover:bg-white/[0.06] transition-all border border-white/[0.08] hover:border-cubus-lime/40 group"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-blue-500 group-hover:bg-cubus-lime"></div>
                        <span className="text-xs text-white/70 truncate group-hover:text-white">
                          {module.name}
                        </span>
                      </div>
                    </button>
                  ))}

                  {day.events.slice(0, 2).map((event) => (
                    <button
                      key={event.id}
                      onClick={() => handleItemClick(event)}
                      className="w-full text-left p-2 rounded bg-yellow-500/10 hover:bg-yellow-500/20 transition-all border border-yellow-500/30 hover:border-yellow-500 group"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-yellow-500"></div>
                        <span className="text-xs text-yellow-400 truncate group-hover:text-yellow-300">
                          {event.title}
                        </span>
                      </div>
                    </button>
                  ))}

                  {(day.modules.length + day.events.length) > 5 && (
                    <p className="text-xs text-subtle px-2 mt-1">
                      +{(day.modules.length + day.events.length) - 5} más
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-white/[0.06] glass-deep flex-shrink-0">
          <h3 className="text-sm font-bold text-white mb-3">Leyenda</h3>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-white/70">Clases programadas</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-white/70">Evaluaciones y fechas límite</span>
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass-surface rounded-xl max-w-2xl w-full border border-white/[0.08] shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
              <h3 className="text-2xl font-bold text-white">
                {isModule(selectedItem) ? selectedItem.name : selectedItem.title}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedItem(null);
                }}
                className="p-2 hover:bg-white/[0.04] rounded-lg transition-all"
              >
                <X className="w-6 h-6 text-muted" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {isModule(selectedItem) ? (
                <>
                  {selectedItem.description && (
                    <div>
                      <p className="text-white/70 leading-relaxed">{selectedItem.description}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="glass-deep rounded-lg p-4">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-medium">Curso</span>
                      </div>
                      <p className="text-white">{selectedItem.courses?.name}</p>
                    </div>

                    <div className="glass-deep rounded-lg p-4">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">Horario</span>
                      </div>
                      <p className="text-white">
                        {fmtTimeRange(selectedItem.start_date, selectedItem.end_date)}
                      </p>
                    </div>
                  </div>

                  {selectedItem.zoom_link && (
                    <a
                      href={selectedItem.zoom_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-cubus-lime text-cubus-bg rounded-lg font-semibold hover:bg-cubus-lime/90 transition-all"
                    >
                      <Video className="w-5 h-5" />
                      Unirse a la clase en Zoom
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </>
              ) : (
                <>
                  {selectedItem.description && (
                    <div>
                      <p className="text-white/70 leading-relaxed">{selectedItem.description}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="glass-deep rounded-lg p-4">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-medium">Curso</span>
                      </div>
                      <p className="text-white">{selectedItem.courses?.name || 'No especificado'}</p>
                    </div>

                    <div className="glass-deep rounded-lg p-4">
                      <div className="flex items-center gap-2 text-muted mb-2">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">Fecha límite</span>
                      </div>
                      <p className="text-white">
                        {fmtTime(selectedItem.start_time)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <FileText className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-yellow-400 font-medium mb-1">Fecha de entrega</p>
                      <p className="text-white/70 text-sm">
                        Asegúrate de completar esta entrega antes de la fecha límite.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
